import React from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card/page-card"
import { PageFilterCard } from "@/components/page-filter-card"
import type { Meter } from "@/models/meter"
import { useSiteLayoutContext } from "@/modules/site/context/site-layout-context"
import {
  DEFAULT_PAGE_SIZE,
  useDataGridVirtualScrollListener,
  useInfiniteScroll,
} from "@/nzds/data-grid"
import { buildBillPeriodsExportPath } from "@/services/bill-period/bill-periods-export-path"
import { useBillPeriodsInfinite } from "@/services/bill-period/use-bill-periods-infinite"
import {
  type BillMeterLookup,
  buildMeterLookupById,
  getUniqueBillMeterIds,
} from "@/services/bill/bill-grid-utils"
import type { BillPeriod } from "@/services/core-api/types"
import { useFlags } from "@/services/feature"
import { useMeters } from "@/services/meter"
import { parseModelId } from "@/utils/parse-model-id"
import { getDisplayValue } from "@/utils/value"

import { Box, Stack } from "@mui/material"
import type { GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

import { SiteBillsEmptyOverlay } from "./site-bills-empty-overlay"
import { SiteBillsErrorOverlay } from "./site-bills-error-overlay"
import { SiteBillsFilters } from "./site-bills-filters"
import { SiteBillsGridToolbar } from "./site-bills-grid-toolbar"
import { SiteBillsMonthlyChart } from "./site-bills-monthly-chart"
import {
  type SiteBillGridRecord,
  useSiteBillsGridColumns,
} from "./use-site-bills-grid-columns"
import { useSiteBillsSelection } from "./use-site-bills-selection"

export const SiteBillsRoute: React.FC = () => {
  const { orgId, site } = useSiteLayoutContext()
  const siteIdNumber = parseModelId(site?.id)

  if (!orgId || !site?.id || siteIdNumber === undefined) {
    return null
  }

  return (
    <SiteBillsRouteContent
      key={site.id}
      organizationId={orgId}
      siteId={site.id}
      siteIdNumber={siteIdNumber}
    />
  )
}

interface SiteBillsRouteContentProps {
  organizationId: string
  siteId: string
  siteIdNumber: number
}

const toSiteBillGridRecord = (
  period: BillPeriod,
  meterLookupById: Map<Meter["id"], BillMeterLookup>
): SiteBillGridRecord => ({
  ...period,
  meter_code: getDisplayValue(
    meterLookupById.get(String(period.meter_id))?.meter_code
  ),
})

interface ExportableBillsCheck {
  downloadHref: string | undefined
  isError: boolean
  isQueryLoading: boolean
  total: number
}

const hasExportableBills = ({
  downloadHref,
  isError,
  isQueryLoading,
  total,
}: ExportableBillsCheck): boolean =>
  Boolean(downloadHref) && !isQueryLoading && !isError && total > 0

const resetVirtualScrollerIfQueryChanged = ({
  gridRoot,
  listQuerySignature,
  previousSignature,
}: {
  gridRoot: HTMLDivElement | null
  listQuerySignature: string
  previousSignature: React.RefObject<string>
}): void => {
  if (previousSignature.current === listQuerySignature) {
    return
  }

  previousSignature.current = listQuerySignature

  const virtualScroller = gridRoot?.querySelector(
    ".MuiDataGrid-virtualScroller"
  )
  if (virtualScroller instanceof HTMLElement) {
    virtualScroller.scrollTop = 0
  }
}

const GRID_HEIGHT = 400

const SiteBillsRouteContent: React.FC<SiteBillsRouteContentProps> = ({
  organizationId,
  siteId,
  siteIdNumber,
}) => {
  const { t } = useTranslation("sites")
  const { site_bills_monthly_chart: showMonthlyChart } = useFlags()
  const gridRootRef = React.useRef<HTMLDivElement | null>(null)
  const selection = useSiteBillsSelection({ organizationId, siteId })
  const columns = useSiteBillsGridColumns({ siteId })
  const listEnabled =
    !selection.selectionIsError &&
    selection.metersAreComplete &&
    selection.siteIdNumber !== undefined &&
    selection.resourceType != null &&
    selection.dateRange != null
  const meterIds = selection.selectedMeter?.id

  const {
    billPeriodsData,
    billPeriodsFetchNextPage,
    billPeriodsHasNextPage,
    billPeriodsIsError,
    billPeriodsIsFetching,
    billPeriodsIsFetchingNextPage,
    billPeriodsIsLoading,
    billPeriodsIsPlaceholderData,
  } = useBillPeriodsInfinite({
    apiQueryOptions: {
      is_interval_derived: false,
      limit: DEFAULT_PAGE_SIZE,
      resource_type: selection.resourceType ?? undefined,
      site_id: siteIdNumber,
      sort: selection.sortParam,
      start_date_from: selection.dateRange?.start,
      start_date_to: selection.dateRange?.end,
      ...(meterIds ? { meter_ids: meterIds } : {}),
    },
    organizationId,
    queryOptions: {
      enabled: listEnabled,
      retry: false,
    },
  })

  const billMeterIds = getUniqueBillMeterIds(billPeriodsData?.pages)
  const { metersData, metersIsPlaceholderData } = useMeters({
    apiQueryOptions: {
      pageSize: billMeterIds.length,
      where: {
        id: billMeterIds,
        organization_id: organizationId,
      },
    },
    queryOptions: {
      enabled: billMeterIds.length > 0,
    },
  })
  const meterLookupById = buildMeterLookupById(
    metersData,
    metersIsPlaceholderData
  )
  const rows = (billPeriodsData?.pages ?? [])
    .flatMap((page) => page.items)
    .map((period) => toSiteBillGridRecord(period, meterLookupById))

  const { handleOnRowsScrollEnd, handleOnScrollTopChange } = useInfiniteScroll({
    fetchNextPage: billPeriodsFetchNextPage,
    hasNextPage: billPeriodsHasNextPage,
    isEnabled: listEnabled,
    isFetchingNextPage: billPeriodsIsFetchingNextPage,
  })

  useDataGridVirtualScrollListener({
    gridRootRef,
    onScroll: handleOnScrollTopChange,
  })

  const listQuerySignature = JSON.stringify({
    end: selection.dateRange?.end,
    meterId: meterIds ?? null,
    resource: selection.resourceType,
    siteId,
    sort: selection.sortParam,
    start: selection.dateRange?.start,
  })
  const previousListQuerySignatureRef = React.useRef(listQuerySignature)

  React.useEffect(() => {
    resetVirtualScrollerIfQueryChanged({
      gridRoot: gridRootRef.current,
      listQuerySignature,
      previousSignature: previousListQuerySignatureRef,
    })
  }, [listQuerySignature])

  const isQueryLoading =
    selection.isSelectionPending ||
    billPeriodsIsLoading ||
    billPeriodsIsPlaceholderData
  const exportQuery = listEnabled
    ? {
        isIntervalDerived: false,
        meterIds,
        resourceType: selection.resourceType ?? undefined,
        siteId: siteIdNumber,
        sort: selection.sortParam,
        startDateFrom: selection.dateRange?.start,
        startDateTo: selection.dateRange?.end,
      }
    : undefined
  const downloadHref = exportQuery
    ? buildBillPeriodsExportPath(organizationId, exportQuery)
    : undefined
  const excelDownloadHref = exportQuery
    ? buildBillPeriodsExportPath(organizationId, {
        ...exportQuery,
        format: "xlsx",
      })
    : undefined
  const canDownload = hasExportableBills({
    downloadHref,
    isError: billPeriodsIsError || selection.selectionIsError,
    isQueryLoading,
    total: billPeriodsData?.pages[0]?.total ?? 0,
  })
  const showChart = Boolean(showMonthlyChart) && listEnabled

  return (
    <Stack spacing={2}>
      <PageFilterCard>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SiteBillsFilters
            anchorEnd={selection.anchorEnd}
            dateRange={selection.dateRange}
            isLoading={selection.isSelectionPending}
            meters={selection.resourceMeters}
            onDateRangeChange={selection.handleDateRangeChange}
            onMeterChange={selection.handleMeterChange}
            onResourceChange={selection.handleResourceChange}
            resourceCodes={selection.resourceCodes}
            resourceType={selection.resourceType}
            selectedMeter={selection.selectedMeter}
          />
        </Box>
      </PageFilterCard>
      {showChart &&
      selection.dateRange &&
      selection.resourceType &&
      selection.siteIdNumber !== undefined ? (
        <PageCard>
          <SiteBillsMonthlyChart
            end={selection.dateRange.end}
            meterId={meterIds ?? null}
            meters={selection.resourceMeters}
            organizationId={organizationId}
            resourceType={selection.resourceType}
            siteId={selection.siteIdNumber}
            start={selection.dateRange.start}
          />
        </PageCard>
      ) : null}
      <PageCard>
        <Box ref={gridRootRef} sx={{ height: GRID_HEIGHT }}>
          <NzDataGrid
            aria-label={t("bills.grid.ariaLabel")}
            columns={columns}
            disableColumnFilter
            disableColumnSelector
            filterMode="server"
            getRowId={(row: SiteBillGridRecord) => row.id}
            loading={
              isQueryLoading ||
              billPeriodsIsFetchingNextPage ||
              (billPeriodsIsFetching && rows.length > 0)
            }
            onRowsScrollEnd={handleOnRowsScrollEnd}
            onSortModelChange={selection.handleSortModelChange}
            rows={rows}
            slotProps={{
              toolbar: {
                canDownload,
                downloadHref,
                excelDownloadHref,
                isRecordCountLoading:
                  selection.isSelectionPending ||
                  billPeriodsIsFetching ||
                  billPeriodsIsPlaceholderData,
                recordCount: billPeriodsData?.pages[0]?.total,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              noRowsOverlay: (billPeriodsIsError || selection.selectionIsError
                ? SiteBillsErrorOverlay
                : SiteBillsEmptyOverlay) as GridSlots["noRowsOverlay"],
              toolbar: SiteBillsGridToolbar as GridSlots["toolbar"],
            }}
            sortModel={selection.sortModel}
            sortingMode="server"
          />
        </Box>
      </PageCard>
    </Stack>
  )
}
